import _ from 'underscore';
import type {
  DataRefId, EntityKey, EntityObject, OverreactSpec,
} from '../public-types';
import type { SchemaNodeLike } from '../schema';
import type { DataRef } from './schema-extension/data-ref';
import { createDataNode, getDataNode } from './schema-extension/schema-extension';
import type { Store } from './store';

export interface SchemaRequestContract {
  getSchemaNode(): SchemaNodeLike;
}

export function getRecordGroup<TEntity extends EntityObject>(
  store: Store<TEntity>,
  requestContract: SchemaRequestContract,
) {
  if (!store) {
    return null;
  }
  const schemaNode = requestContract.getSchemaNode();
  return store.getRecordGroup(schemaNode.modelName as string);
}

export function getRecordsById<TEntity extends EntityObject>(
  store: Store<TEntity>,
  requestContract: SchemaRequestContract,
  ids: string[],
) {
  return getRecordGroup(store, requestContract)?.getRecords(ids) ?? [];
}

export function getRecordsByEntityKey<
  TEntity extends EntityObject,
  TVariables extends object,
  TData,
  TResult,
>(
  store: Store<TEntity>,
  spec: OverreactSpec<TVariables, TData, TResult, TEntity>,
  keys: EntityKey[],
) {
  const recordGroup = getRecordGroup(store, spec.requestContract);
  return recordGroup?.getRecordsByEntityKeys(
    spec.responseContract.keySelector as (_entity: TEntity) => EntityKey,
    keys,
  ) ?? [];
}

export function getRecordsFromResponseDataArray<
  TEntity extends EntityObject,
  TVariables extends object,
  TData,
  TResult,
>(
  store: Store<TEntity>,
  spec: OverreactSpec<TVariables, TData, TResult, TEntity>,
  dataArray: TEntity[],
) {
  const keySelector = spec.responseContract.keySelector as (_entity: TEntity) => EntityKey;
  return getRecordsByEntityKey(store, spec, _.map(dataArray, keySelector));
}

export function getRecords<TEntity extends EntityObject>(
  store: Store<TEntity>,
  requestContract: SchemaRequestContract,
  key: DataRefId,
) {
  if (!store) {
    return null;
  }
  const schemaNode = requestContract.getSchemaNode();
  const dataNode = getDataNode(schemaNode) || createDataNode(schemaNode, store);
  const recordGroup = getRecordGroup(store, requestContract);
  return recordGroup?.getRecords(dataNode.getDataRef(key).idRefs) ?? [];
}

export function getDataRef(
  store: Store,
  requestContract: SchemaRequestContract,
  dataRefId: DataRefId,
) {
  if (!store) {
    return null;
  }
  const schemaNode = requestContract.getSchemaNode();
  const dataNode = getDataNode(schemaNode) || createDataNode(schemaNode, store);
  return dataNode.getDataRef(dataRefId);
}

export function updateDataRefWithIds(dataRef: DataRef, ids: string[]) {
  dataRef.clear();
  ids.forEach(id => dataRef.add(id));
  dataRef.onUpdate(ids);
}
